<template>
    <div class="intro-holder">
        <div class="swiper mySwiper anim" ref="carousel">
            <div class="swiper-wrapper">
                <slot
                    :slidesPerView="props.slidesPerView"
                    :showPagination="false"
                />
            </div>
        </div>
        <div
            :class="[
                'home-carousel-fruits',
                {
                    active: fruits && displayFruit,
                    [`fruit-${displayFruitKey}`]: displayFruitKey,
                },
            ]"
            :style="{ '--fruit-background': fruitBackground }"
            aria-hidden="true"
        >
            <template v-if="showFruitImages">
                <Transition name="home-carousel-bg">
                    <img
                        v-if="displayFruit"
                        :key="displayFruit.key"
                        :src="displayFruit.bgimg"
                        loading="lazy"
                        decoding="async"
                        class="bg-img"
                    />
                </Transition>
                <img
                    v-for="index in 5"
                    :key="`${displayFruit.key}-${index}`"
                    :class="['home-carousel-fruit', `fruit-${index}`]"
                    :src="displayFruit.src"
                    alt=""
                    loading="lazy"
                    decoding="async"
                >
            </template>
        </div>
    </div>
</template>

<script setup>
import Swiper from 'swiper'
import { EffectCoverflow } from 'swiper/modules'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const carousel = ref(null)
const isMobile = ref(false)
const fruits = ref(false)
const activeFruitKey = ref(null)
const displayFruitKey = ref(null)

let swiperInstance = null
let handleResize = null
let hoveredSlide = null
let hoverTimer = null
let hideFruitTimer = null
let hasMobileMoved = false

const HOVER_DELAY = 300
const FRUIT_FADE_DURATION = 300
const DESKTOP_QUERY = '(min-width: 768px)'

const props = defineProps({
    slidesPerView: {
        type: Number,
        default: 9,
    },
})

const FRUITS = [
  {
    key: 'apple',
    aliases: ['apple'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON_Apple.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/apple.webp',
    alt: 'Apple fruit',
    background: '#42A74E',
  },
  {
    key: 'applemango',
    aliases: ['apple mango', 'applemango', 'apple-mango'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON_AppleMango.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/applemango.webp',
    alt: 'Apple Mango fruit',
    background: '#D52A33',
  },
  {
    key: 'citron',
    aliases: ['citron'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/citron.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/citron.webp',
    alt: 'Citron fruit',
    background: '#FE8F1D',
  },
  {
    key: 'grape',
    aliases: ['grape'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON_Grape.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/grape.webp',
    alt: 'Grape fruit',
    background: '#00B7BF',
  },
  {
    key: 'lychee',
    aliases: ['lychee'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON_Lychee.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/lychee.webp',
    alt: 'Lychee fruit',
    background: '#F26178',
  },
  {
    key: 'peach',
    aliases: ['peach'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON_Peach.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/peach.webp',
    alt: 'Peach fruit',
    background: '#F27898',
  },
  {
    key: 'plum',
    aliases: ['plum'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON_Plum.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/plum.webp',
    alt: 'Plum fruit',
    background: '#510C76',
  },
  {
    key: 'strawberry',
    aliases: ['strawberry'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON_Strawberry.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/strawberry.webp',
    alt: 'Strawberry fruit',
    background: '#D3236F',
  },
  {
    key: 'yogurt',
    aliases: ['yogurt'],
    src: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/fruit/SOON-Yogurt.webp',
    bgimg: 'https://d2q6ite07t3u1l.cloudfront.net/public/soonhari2026/dev/Uploads/home/yogurt.webp',
    alt: 'Yogurt fruit',
    background: '#F57E35',
  },
]

const activeFruit = computed(() => FRUITS.find((fruit) => fruit.key === activeFruitKey.value) || null)
const displayFruit = computed(() => FRUITS.find((fruit) => fruit.key === displayFruitKey.value) || null)
const fruitBackground = computed(() => displayFruit.value?.background || 'var(--background-color-change)')
const showFruitImages = computed(() => fruits.value && displayFruit.value)

const normalize = (value = '') => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const fruitCandidates = [...FRUITS].sort((a, b) => b.key.length - a.key.length)

const getFruitFromSlide = (slide) => {
    if (!slide) return null

    const textToMatch = [
        slide.querySelector('img')?.alt,
        slide.getAttribute('aria-label'),
        slide.className,
        ...slide.classList,
    ]
        .filter(Boolean)
        .join(' ')

    const normalizedText = normalize(textToMatch)

    return fruitCandidates.find((fruit) => fruit.aliases.some((alias) => normalizedText.includes(normalize(alias)))) || null
}

const cancelPendingFruitHover = () => {
    window.clearTimeout(hoverTimer)
    hoverTimer = null
    hoveredSlide = null
}

const clearFruitHover = ({ clearDisplay = false } = {}) => {
    cancelPendingFruitHover()
    window.clearTimeout(hideFruitTimer)
    fruits.value = false
    activeFruitKey.value = null

    if (clearDisplay) {
        displayFruitKey.value = null
        return
    }

    hideFruitTimer = window.setTimeout(() => {
        if (!fruits.value) {
            displayFruitKey.value = null
        }
    }, FRUIT_FADE_DURATION)
}

const activateFruit = (fruit) => {
    if (!fruit) return

    window.clearTimeout(hideFruitTimer)
    activeFruitKey.value = fruit.key
    displayFruitKey.value = fruit.key
    fruits.value = true
}

const isDesktopHover = () => window.matchMedia(DESKTOP_QUERY).matches

const activateMobileSlideFruit = (swiper = swiperInstance) => {
    if (!isMobile.value || !hasMobileMoved || !swiper) return

    activateFruit(getFruitFromSlide(swiper.slides?.[swiper.activeIndex]))
}

const queueFruitHover = (slide) => {
    if (!isDesktopHover()) return
    if (slide === hoveredSlide) return

    window.clearTimeout(hoverTimer)
    window.clearTimeout(hideFruitTimer)
    hoveredSlide = slide

    const fruit = getFruitFromSlide(slide) 

    if (!fruit) {
        cancelPendingFruitHover()
        return
    }

    hoverTimer = window.setTimeout(() => {
        if (!hoveredSlide || hoveredSlide !== slide || !isDesktopHover()) return

        activateFruit(fruit)
    }, HOVER_DELAY)
}

const handlePointerOver = (event) => {
    const slide = event.target.closest?.('.swiper-slide')

    if (!carousel.value?.contains(slide)) return

    queueFruitHover(slide)
}

const handlePointerOut = (event) => {
    const slide = event.target.closest?.('.swiper-slide')

    if (!slide || slide !== hoveredSlide) return
    if (slide.contains(event.relatedTarget)) return

    cancelPendingFruitHover()
}

const getConfig = () => ({
    modules: [EffectCoverflow],
    effect: 'coverflow',
    grabCursor: true,
    centeredSlides: true,
    slidesPerView: 5,
    initialSlide: 4,
    threshold: 8,
    touchStartPreventDefault: false,

    preventClicks: true,
    preventClicksPropagation: false, 
    touchReleaseOnEdges: true,


    coverflowEffect: {
        rotate: 0,
        stretch: 20,
        depth: 200,
        modifier: 1,
        slideShadows: false,
    },

    // autoplay: {
    //     delay: 2500,
    //     disableOnInteraction: false,
    // },

    on: {
        sliderFirstMove() {
            hasMobileMoved = true
        },
        slideChange(swiper) {
            activateMobileSlideFruit(swiper)
        },
        touchEnd(swiper) {
            activateMobileSlideFruit(swiper)
        },
    },

})

const createSwiper = async () => {
    await nextTick()

    if (!carousel.value) return
    if (swiperInstance) return

    hasMobileMoved = false
    swiperInstance = new Swiper(carousel.value, getConfig())
}

const destroySwiper = () => {
    hasMobileMoved = false
    swiperInstance?.destroy(true, true)
    swiperInstance = null
}

const syncSwiperToViewport = async () => {
    isMobile.value = window.innerWidth < 768

    if (isMobile.value) {
        clearFruitHover()
        await createSwiper()
        return
    }

    destroySwiper()
}

onMounted(async () => {
    await syncSwiperToViewport()

    handleResize = () => {
        void syncSwiperToViewport()
    }

    window.addEventListener('resize', handleResize)
    carousel.value?.addEventListener('pointerover', handlePointerOver)
    carousel.value?.addEventListener('pointerout', handlePointerOut)
})

onBeforeUnmount(() => {
    clearFruitHover({ clearDisplay: true })

    if (handleResize) {
        window.removeEventListener('resize', handleResize)
    }

    carousel.value?.removeEventListener('pointerover', handlePointerOver)
    carousel.value?.removeEventListener('pointerout', handlePointerOut)

    destroySwiper()
})
</script>
